import {
  MaSegment,
  dbToGain,
  effectiveClipDurationSeconds,
  maSegmentDisplaySeconds,
} from './maTypes';
import { decodeMediaItemAudio, getSharedAudioContext } from './useMaAudioEngine';

/**
 * 再生と同じ規則で動画表示区間の表示上の終了時刻を求める。
 * 区間 i の終了 = 次区間の開始（最終区間は動画表示尺まで）
 */
function spanDisplayEnd(
  spans: MaSegment['videoSpans'],
  index: number,
  videoLen: number,
): number {
  const next = spans[index + 1];
  return next ? Math.min(next.startSeconds, videoLen) : videoLen;
}

/**
 * MA セグメントのオーディオミックスレンダリング。
 *
 * 再生エンジン（useMaAudioEngine）と同じミックス（動画内蔵オーディオ +
 * 全クリップの gain / トリム / 後着優先カット / テール）を
 * OfflineAudioContext でレンダリングし、WAV（48kHz / 24bit / stereo）を生成する。
 *
 * 「レンダリング書き出し（WAV）」のダウンロードに使うほか、
 * 動画書き出し（renderMaSegmentVideo）の音声トラック生成にも使われる。
 */

const RENDER_SAMPLE_RATE = 48000;
const RENDER_CHANNELS = 2;

export interface RenderMaSegmentResult {
  blob: Blob;
  /** 音声をデコードできず除外したファイル名（音声トラックなしの動画を含む） */
  skippedFiles: string[];
}

export async function renderMaSegmentToWav(segment: MaSegment): Promise<RenderMaSegmentResult> {
  const decodeCtx = getSharedAudioContext();
  if (!decodeCtx) throw new Error('Web Audio API が利用できません');

  const skippedFiles: string[] = [];

  // ---- デコード（動画音声 + ユニークなオーディオファイル） ----
  // 音声トラックのない動画・デコード不能ファイルはスキップして続行する。
  // 動画音声は表示区間（videoSpans）ごとのファイルをユニークにデコードする
  const spans = segment.videoSpans;
  const videoBuffers = new Map<string, AudioBuffer>();
  for (const span of spans) {
    const id = span.videoItem.id;
    if (videoBuffers.has(id)) continue;
    try {
      videoBuffers.set(id, await decodeMediaItemAudio(span.videoItem, decodeCtx));
    } catch {
      if (!skippedFiles.includes(span.videoItem.file_name)) {
        skippedFiles.push(span.videoItem.file_name);
      }
    }
  }

  const clipBuffers = new Map<string, AudioBuffer>();
  for (const clip of segment.clips) {
    const id = clip.audioItem.id;
    if (clipBuffers.has(id)) continue;
    try {
      clipBuffers.set(id, await decodeMediaItemAudio(clip.audioItem, decodeCtx));
    } catch {
      skippedFiles.push(clip.audioItem.file_name);
    }
  }

  // ---- 全体尺の決定（動画表示尺 vs クリップ末尾 = テール含む） ----
  // 表示尺（候補による延長込み）が CSV 未指定の場合は最終区間のファイル実尺から換算する
  const lastSpan = spans[spans.length - 1];
  const lastBuffer = videoBuffers.get(lastSpan.videoItem.id);
  const videoLen =
    maSegmentDisplaySeconds(segment) ??
    (lastBuffer
      ? Math.max(0, lastSpan.startSeconds + (lastBuffer.duration - lastSpan.fileOffsetSeconds))
      : 0);
  const clipsEnd = segment.clips.reduce((max, clip) => {
    const buffer = clipBuffers.get(clip.audioItem.id);
    if (!buffer) return max;
    return Math.max(max, clip.startSeconds + effectiveClipDurationSeconds(clip, buffer.duration));
  }, 0);
  const total = Math.max(videoLen, clipsEnd);
  if (total <= 0) throw new Error('レンダリング対象の音声がありません');

  // ---- オフラインレンダリング ----
  const offline = new OfflineAudioContext(
    RENDER_CHANNELS,
    Math.ceil(total * RENDER_SAMPLE_RATE),
    RENDER_SAMPLE_RATE,
  );

  // 動画音声を表示区間ごとに配置（再生時の videoFileTime = fileOffset + (t - start) と同じ）
  spans.forEach((span, i) => {
    const buffer = videoBuffers.get(span.videoItem.id);
    if (!buffer) return;
    const displayEnd = spanDisplayEnd(spans, i, videoLen);
    const duration = Math.min(
      displayEnd - span.startSeconds,
      Math.max(0, buffer.duration - span.fileOffsetSeconds),
    );
    if (duration <= 0) return;
    const source = offline.createBufferSource();
    source.buffer = buffer;
    source.connect(offline.destination);
    source.start(span.startSeconds, span.fileOffsetSeconds, duration);
  });

  segment.clips.forEach(clip => {
    const buffer = clipBuffers.get(clip.audioItem.id);
    if (!buffer) return;
    const duration = effectiveClipDurationSeconds(clip, buffer.duration);
    if (duration <= 0) return; // 同時開始カットで全消しされたクリップ
    const source = offline.createBufferSource();
    source.buffer = buffer;
    const gain = offline.createGain();
    gain.gain.value = dbToGain(clip.gainDb);
    source.connect(gain);
    gain.connect(offline.destination);
    source.start(clip.startSeconds, 0, duration);
  });

  const rendered = await offline.startRendering();
  return { blob: encodeWav24(rendered), skippedFiles };
}

/**
 * AudioBuffer を 24bit PCM WAV に変換する。
 * ミックスでピークが 0dBFS を超えた場合はクリップ歪みを避けるため
 * 全体をピークが 0dBFS に収まるようスケールダウンする。
 */
function encodeWav24(buffer: AudioBuffer): Blob {
  const channels = buffer.numberOfChannels;
  const frames = buffer.length;
  const channelData = Array.from({ length: channels }, (_, ch) => buffer.getChannelData(ch));

  let peak = 0;
  channelData.forEach(data => {
    for (let i = 0; i < frames; i += 1) {
      const abs = Math.abs(data[i]);
      if (abs > peak) peak = abs;
    }
  });
  const scale = peak > 1 ? 1 / peak : 1;

  const bytesPerSample = 3; // 24bit
  const blockAlign = channels * bytesPerSample;
  const dataSize = frames * blockAlign;
  const arrayBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(arrayBuffer);

  const writeAscii = (pos: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(pos + i, text.charCodeAt(i));
  };

  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt チャンクサイズ
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * blockAlign, true); // byte rate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 24, true); // bits per sample
  writeAscii(36, 'data');
  view.setUint32(40, dataSize, true);

  let pos = 44;
  for (let i = 0; i < frames; i += 1) {
    for (let ch = 0; ch < channels; ch += 1) {
      const sample = Math.max(-1, Math.min(1, channelData[ch][i] * scale));
      const value = Math.round(sample * 8388607); // 2^23 - 1
      view.setUint8(pos, value & 0xff);
      view.setUint8(pos + 1, (value >> 8) & 0xff);
      view.setUint8(pos + 2, (value >> 16) & 0xff);
      pos += 3;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}
